import {
  Archive,
  ArchiveRestore,
  Bell,
  Bold,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleQuestionMark,
  CircleX,
  Clock,
  Code,
  Copy,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Eye,
  File,
  Flag,
  Folder,
  Forward,
  Funnel,
  GripVertical,
  Inbox,
  Info,
  Italic,
  Link,
  List,
  ListOrdered,
  LogOut,
  Mail,
  MailOpen,
  Maximize2,
  Menu as MenuGlyph,
  MessageSquareText,
  Minimize2,
  Monitor,
  Moon,
  PanelLeft,
  PanelRight,
  Paperclip,
  PenLine,
  Pin,
  Plus,
  Printer,
  RefreshCw,
  RemoveFormatting,
  Reply,
  ReplyAll,
  Search,
  Palette,
  Send,
  Settings,
  SlidersHorizontal,
  Smartphone,
  Star,
  Sun,
  Tag,
  TextQuote,
  Trash,
  TriangleAlert,
  Underline,
  Undo2,
  Unlink,
  User,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cx } from '../internal/cx';

/**
 * Every glyph the design system offers. The union is written out rather than derived from the map
 * below, so the public type stays a plain string union and no icon-library type reaches the API.
 * Adding a glyph means one import, one entry in `GLYPHS` and one member here.
 */
export type IconName =
  | 'archive'
  | 'attachment'
  | 'bell'
  | 'bold'
  | 'check'
  | 'check-all'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'clear-format'
  | 'clock'
  | 'close'
  | 'code'
  | 'compose'
  | 'copy'
  | 'delete'
  | 'download'
  | 'draft'
  | 'error'
  | 'external-link'
  | 'eye'
  | 'filter'
  | 'flag'
  | 'folder'
  | 'forward'
  | 'grip'
  | 'help'
  | 'inbox'
  | 'info'
  | 'italic'
  | 'junk'
  | 'link'
  | 'list-bullet'
  | 'list-ordered'
  | 'logout'
  | 'mail'
  | 'mail-open'
  | 'maximise'
  | 'menu'
  | 'message'
  | 'minimise'
  | 'monitor'
  | 'moon'
  | 'more'
  | 'more-vertical'
  | 'pane-left'
  | 'pane-right'
  | 'pin'
  | 'plus'
  | 'print'
  | 'quote'
  | 'refresh'
  | 'reply'
  | 'reply-all'
  | 'search'
  | 'palette'
  | 'send'
  | 'settings'
  | 'sliders'
  | 'smartphone'
  | 'star'
  | 'success'
  | 'sun'
  | 'tag'
  | 'unarchive'
  | 'underline'
  | 'undo'
  | 'unlink'
  | 'user'
  | 'users'
  | 'warning';

/** Icon sizes in px: inline with the text, the default control glyph, and a section heading glyph. */
export type IconSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Readonly<Record<IconSize, number>> = { sm: 14, md: 16, lg: 20 };

/** Stroke width that keeps the 16 px glyph legible without looking heavy at 20 px. */
const STROKE_WIDTH = 1.75;

const GLYPHS: Readonly<Record<IconName, LucideIcon>> = {
  archive: Archive,
  attachment: Paperclip,
  bell: Bell,
  bold: Bold,
  check: Check,
  'check-all': CheckCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'clear-format': RemoveFormatting,
  clock: Clock,
  close: X,
  code: Code,
  compose: PenLine,
  copy: Copy,
  delete: Trash,
  download: Download,
  draft: File,
  error: CircleAlert,
  'external-link': ExternalLink,
  eye: Eye,
  filter: Funnel,
  flag: Flag,
  folder: Folder,
  forward: Forward,
  grip: GripVertical,
  help: CircleQuestionMark,
  inbox: Inbox,
  info: Info,
  italic: Italic,
  junk: CircleX,
  link: Link,
  'list-bullet': List,
  'list-ordered': ListOrdered,
  logout: LogOut,
  mail: Mail,
  'mail-open': MailOpen,
  maximise: Maximize2,
  menu: MenuGlyph,
  message: MessageSquareText,
  minimise: Minimize2,
  monitor: Monitor,
  moon: Moon,
  more: Ellipsis,
  'more-vertical': EllipsisVertical,
  'pane-left': PanelLeft,
  'pane-right': PanelRight,
  pin: Pin,
  plus: Plus,
  print: Printer,
  quote: TextQuote,
  refresh: RefreshCw,
  reply: Reply,
  'reply-all': ReplyAll,
  search: Search,
  palette: Palette,
  send: Send,
  settings: Settings,
  sliders: SlidersHorizontal,
  smartphone: Smartphone,
  star: Star,
  success: CircleCheck,
  sun: Sun,
  tag: Tag,
  unarchive: ArchiveRestore,
  underline: Underline,
  undo: Undo2,
  unlink: Unlink,
  user: User,
  users: Users,
  warning: TriangleAlert,
};

/** Props of {@link Icon}. */
export interface IconProps {
  /** Which glyph to draw. */
  name: IconName;
  /** Glyph size; `md` (16 px) by default. */
  size?: IconSize;
  /**
   * Accessible name. Give it only when the icon carries meaning of its own; without it the icon is
   * decorative and hidden from assistive technology, which is right next to a visible label.
   */
  title?: string;
  /** Extra utility classes. The colour is inherited from the text colour unless one is given. */
  className?: string;
}

/**
 * A glyph from the icon set, sized and coloured by the design tokens.
 *
 * @example <Icon name="archive" /> <span>Arşivle</span>
 * @example <Icon name="error" title={t('mail.sendFailed')} className="text-danger" />
 */
export function Icon({ name, size = 'md', title, className }: IconProps) {
  const Glyph = GLYPHS[name];
  const px = SIZE_PX[size];
  return (
    <Glyph
      width={px}
      height={px}
      strokeWidth={STROKE_WIDTH}
      className={cx('shrink-0', className)}
      focusable="false"
      aria-hidden={title === undefined || undefined}
      role={title === undefined ? undefined : 'img'}
      aria-label={title}
    />
  );
}
